import {
  useReducer,
  useMemo,
  useCallback,
  createContext,
  useContext,
} from 'react';
import PropTypes from 'prop-types';

import { ip } from '../../utils/functions/urls';
import getRequestMeta from '../../utils/functions/generateMeta';
import { error as errorLabel } from '../../label';

import { AuthContext } from '../AuthProvider';

const initialState = {
  isLoading: false,
  tutorials: [],
  currentTutorial: null,
};

const reduce = (state, action) => {
  switch (action.type) {
    case 'setLoading':
      return {
        ...state,
        isLoading: action.isLoading,
      };

    case 'getTutorials':
      return {
        ...state,
        isLoading: false,
        tutorials: action.tutorials,
      };

    case 'getTutorial':
      return {
        ...state,
        isLoading: false,
        currentTutorial: action.tutorial,
      };

    case 'createTutorial':
      return {
        ...state,
        isLoading: false,
        tutorials: [...state.tutorials, action.tutorial],
      };

    case 'updateTutorial':
      return {
        ...state,
        isLoading: false,
        tutorials: state.tutorials.map((tutorial) => (
          tutorial.tutorialId === action.tutorial.tutorialId
            ? action.tutorial
            : tutorial
        )),
        currentTutorial: state.currentTutorial?.tutorialId === action.tutorial.tutorialId
          ? action.tutorial
          : state.currentTutorial,
      };

    case 'deleteTutorial':
      return {
        ...state,
        isLoading: false,
        tutorials: state.tutorials.filter((tutorial) => tutorial.tutorialId !== action.id),
        currentTutorial: state.currentTutorial?.tutorialId === action.id
          ? null
          : state.currentTutorial,
      };

    case 'clearCurrentTutorial':
      return {
        ...state,
        currentTutorial: null,
      };

    default:
      return state;
  }
};

export const TutorialContext = createContext(null);

const TutorialProvider = ({ children = null }) => {
  const [state, dispatch] = useReducer(reduce, initialState);
  const { currentUser } = useContext(AuthContext);

  const setLoading = (isLoading = true) => {
    dispatch({ type: 'setLoading', isLoading });
  };

  const getToken = useCallback(async () => {
    if (!currentUser) throw new Error('Usuário não autenticado.');

    return currentUser.getIdToken();
  }, [currentUser]);

  const request = useCallback(async (endpoint, method = 'GET', body = null) => {
    try {
      const token = await getToken();

      const options = {
        ...(await getRequestMeta(token, method, 'JSON')),
      };

      if (body) {
        options.body = JSON.stringify(body);
      }

      const response = await fetch(`${ip}${endpoint}`, options);

      const raw = await response.json().catch(() => null);

      if (!response.ok) {
        return {
          error: true,
          msg: raw?.message || errorLabel.fetchGeneric,
          raw,
        };
      }

      return {
        error: false,
        msg: null,
        data: raw,
      };
    } catch (er) {
      console.log(er);

      return {
        error: true,
        msg: errorLabel.fetchGeneric,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [getToken]);

  const getTutorials = useCallback(async () => {
    const res = await request('/tutorials');

    return {
      ...res,
      tutorials: res.data?.tutorials || [],
    };
  }, [request]);

  const getTutorial = useCallback(async (id) => {
    const res = await request(`/tutorials/${id}`);

    return {
      ...res,
      tutorial: res.data?.tutorial || null,
    };
  }, [request]);

  const createTutorial = useCallback(async ({
    url,
    title,
    description = '',
    group,
  }) => {
    const res = await request('/tutorials', 'POST', {
      url,
      title,
      description,
      group,
    });

    return {
      ...res,
      tutorial: res.data?.tutorial || null,
    };
  }, [request]);

  const updateTutorial = useCallback(async (id, {
    url,
    title,
    description = '',
    group,
  }) => {
    console.log(id)
    const res = await request(`/tutorials/${id}`, 'PUT', {
      url,
      title,
      description,
      group,
    });

    return {
      ...res,
      tutorial: res.data?.tutorial || null,
    };
  }, [request]);

  const deleteTutorial = useCallback(async (id) => request(`/tutorials/${id}`, 'DELETE'), [request]);

  const middleware = useCallback(async (action) => {
    switch (action.type) {
      case 'getTutorials': {
        setLoading();

        const res = await getTutorials();

        if (!res.error) {
          dispatch({ type: 'getTutorials', tutorials: res.tutorials });
        } else {
          setLoading(false);
        }

        return res;
      }

      case 'getTutorial': {
        setLoading();

        const res = await getTutorial(action.id);

        if (!res.error) {
          dispatch({ type: 'getTutorial', tutorial: res.tutorial });
        } else {
          setLoading(false);
        }

        return res;
      }

      case 'createTutorial': {
        setLoading();

        const res = await createTutorial(action.tutorial);

        if (!res.error && res.tutorial) {
          dispatch({ type: 'createTutorial', tutorial: res.tutorial });
        } else {
          setLoading(false);
        }

        return res;
      }

      case 'updateTutorial': {
        setLoading();
        console.log(action)
        const res = await updateTutorial(action.id, action.tutorial);

        if (!res.error && res.tutorial) {
          dispatch({ type: 'updateTutorial', tutorial: res.tutorial });
        } else {
          setLoading(false);
        }

        return res;
      }

      case 'deleteTutorial': {
        setLoading();

        const res = await deleteTutorial(action.id);

        if (!res.error) {
          dispatch({ type: 'deleteTutorial', id: action.id });
        } else {
          setLoading(false);
        }

        return res;
      }

      default:
        return dispatch(action);
    }
  }, [
    getTutorials,
    getTutorial,
    createTutorial,
    updateTutorial,
    deleteTutorial,
  ]);

  const tutorialAPI = useMemo(() => ({
    getTutorials: () => middleware({ type: 'getTutorials' }),

    getTutorial: (id) => middleware({
      type: 'getTutorial',
      id,
    }),

    createTutorial: (tutorial) => middleware({
      type: 'createTutorial',
      tutorial,
    }),

    updateTutorial: (id, tutorial) => middleware({
      type: 'updateTutorial',
      id,
      tutorial,
    }),

    deleteTutorial: (id) => middleware({
      type: 'deleteTutorial',
      id,
    }),

    clearCurrentTutorial: () => middleware({
      type: 'clearCurrentTutorial',
    }),
  }), [middleware]);

  return (
    <TutorialContext.Provider value={{ state, tutorialAPI }}>
      {children}
    </TutorialContext.Provider>
  );
};

TutorialProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    PropTypes.element,
  ]),
};

export default TutorialProvider;
